import React, { useEffect, useState, useRef, Children, Fragment, Component } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, LayoutGridIcon, WrenchIcon, ChevronRightIcon, ChevronLeftIcon } from 'lucide-react';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
interface ProjectDetailProps {
  title: string;
  gridLineColor?: string;
}
export function ProjectDetail({
  title,
  gridLineColor = 'rgba(185,210,214,0.5)'
}: ProjectDetailProps) {
  const scrollRef1 = useRef<HTMLDivElement>(null);
  const scrollRef2 = useRef<HTMLDivElement>(null);
  const [atEnd1, setAtEnd1] = useState(false);
  const [atEnd2, setAtEnd2] = useState(false);
  const checkScrollEnd = (el: HTMLDivElement) => {
    return el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
  };
  useEffect(() => {
    const el = scrollRef1.current;
    if (!el) return;
    const handleScroll = () => {
      setAtEnd1(checkScrollEnd(el));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const el = scrollRef2.current;
    if (!el) return;
    const handleScroll = () => {
      setAtEnd2(checkScrollEnd(el));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollRight = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: 380,
        behavior: 'smooth'
      });
    }
  };
  const scrollLeft = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: -380,
        behavior: 'smooth'
      });
    }
  };
  // Scroll to top when mounting
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.19, 1, 0.22, 1]
      }
    }
  };
  return <motion.article variants={container} initial="hidden" animate="show" className="w-full pb-[0px]" data-id="element-211" data-mp="svoh3bc0i4k">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-16 md:mb-24 text-center pt-12" data-id="element-212" data-mp="3s0zodskpqytc">
        <motion.h1 variants={item} className="text-4xl md:text-6xl font-bold text-ink tracking-tight mb-6" data-id="element-213" data-mp="34b5dvhnv9mc9">
          {title}
        </motion.h1>
        <motion.p variants={item} className="text-xl md:text-2xl text-stone-medium font-light leading-relaxed max-w-3xl mx-auto mb-16" data-id="element-214" data-mp="3e81rg1smgyno">
          Led the creation of a scalable decision system that unified fragmented
          guidance across 10+ teams, improving consistency and advertiser
          adoption.
        </motion.p>

        {/* Metadata Row */}
        <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left border-t border-b border-stone-border py-8" data-id="element-215" data-mp="26eiamemn38l6">
          <div data-id="element-216" data-mp="1xf4ytfb1m7xa">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-217" data-mp="1xfn38z7uzsga">My role</h3>
            <p className="text-stone-medium text-sm" data-id="element-218" data-mp="22dojlcdwhdo2">Lead Product Designer</p>
          </div>
          <div data-id="element-219" data-mp="2q3itpm6u4o4m">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-220" data-mp="2q30qu6po3bnq">Time frame</h3>
            <p className="text-stone-medium text-sm" data-id="element-221" data-mp="2q5wri2swgefv">2023 - 2024 (10 months)</p>
          </div>
          <div data-id="element-222" data-mp="2u6yoeczcgeeo">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-223" data-mp="2gdzaugj4gu9r">Platform</h3>
            <p className="text-stone-medium text-sm" data-id="element-224" data-mp="2gbmvmb4r0y0e">Desktop Web, Mobile App</p>
          </div>
        </motion.div>
      </div>

      {/* Hero Image */}
      <motion.div variants={item} className="w-full mb-16 md:mb-24 bg-[#F6F9FE]" style={{
      backgroundImage: `linear-gradient(${gridLineColor} 1px, transparent 1px), linear-gradient(90deg, ${gridLineColor} 1px, transparent 1px)`,
      backgroundSize: '21px 21px'
    }} data-id="element-225" data-mp="3924zvfh7l5fe">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-0 pt-[8px]" data-id="element-226" data-mp="1k6nvfdaxoi0z">
          <div className="w-full aspect-[16/9] md:aspect-[21/9] rounded-t-2xl overflow-hidden relative" data-id="element-227" data-mp="1g5u92od53ba7">
            <img src="https://cdn.magicpatterns.com/uploads/jthGTqJQpayPmnE56hjjnM/UGF_hero.png" alt="Unified Guidance Framework Hero" className="w-full h-full object-cover object-top" data-id="element-228" data-mp="1b8kmdgh4xdq2" />
          </div>
        </div>
      </motion.div>

      {/* Paragraph Only Modules */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 mb-24 space-y-16" data-id="element-229" data-mp="1z3wz96hfbe9l">
        <motion.section variants={item} data-id="element-230" data-mp="1u5c25fxgxapd">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-231" data-mp="1u4aod3p2ia3y">Context</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-232" data-mp="2rmkfo8lh654c">
            Guidance helps advertisers improve campaign performance, and over
            time many teams built guidance across different Ads surfaces. These
            efforts evolved independently without a shared framework, resulting
            in fragmented guidance experiences.
          </p>
        </motion.section>

        <motion.section variants={item} data-id="element-233" data-mp="343bv9kgm64xp">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-234" data-mp="346r9ym6dlbyl">Challenge</h2>

          <ul className="list-disc pl-5 text-stone-medium space-y-4" data-id="element-235" data-mp="387ji1c3ihata">
            <li data-id="element-236" data-mp="3d5upjg6c398c">
              <span className="font-bold text-ink" data-id="element-237" data-mp="3d49uzqk0rqah">
                For advertisers: Low trust and adoption of guidance
              </span>
              <br data-id="element-238" data-mp="3muw7hbwfw0oy" />
              Advertisers struggled to understand the impact of guidance and
              integrate it into their workflow, resulting in low trust and low
              adoption.
            </li>
            <li data-id="element-239" data-mp="3vvue81y9imb8">
              <span className="font-bold text-ink" data-id="element-240" data-mp="3vwc8wu5a0sh1">
                For Meta teams: Lack of a unified system
              </span>
              <br data-id="element-241" data-mp="4psvr8bywqfk" />
              Guidance creation and distribution were decentralized across
              teams, with no shared framework or governance to scale guidance
              consistently.
            </li>
          </ul>
          <div className="flex flex-col gap-3 mt-6 items-center relative left-1/2 -translate-x-1/2 w-[1100px] max-w-[90vw]" data-id="element-242" data-mp="1wozrkn8yoe1a">
            <div className="w-full rounded-md border border-stone-border overflow-hidden" data-id="element-243" data-mp="25qz698wi7612">
              <img src="https://cdn.magicpatterns.com/uploads/xt5eT33A1R9HBXsmXCGbZq/Fragmented_guidance_2.jpg" alt="Fragmented guidance patterns across surfaces" className="w-full h-auto block object-contain" data-id="element-244" data-mp="2kg57f2ewj6bl" />
            </div>
            <p className="text-sm text-stone-medium font-medium" data-id="element-245" data-mp="2oi8gxli0g0u8">
              Fragmented guidance patterns across surfaces
            </p>
          </div>
        </motion.section>
      </div>

      {/* My Role Section */}

      {/* Build to scale (duplicated above Approach) */}
      <motion.section variants={item} className="mb-32 mt-[128px] bg-[#F3F4F5] pt-[64px]" data-id="element-246" data-mp="u6bwwdlwdx97">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-12" data-id="element-247" data-mp="3aqe5rwuwketx">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-248" data-mp="3asi84ad5l5s3">Project goal </h2>
          <p className="text-stone-medium mb-8" data-id="element-249" data-mp="2wyhiq783cv4e">
            Create a unified framework and governance model to improve
            advertiser adoption and enable scalable, consistent guidance across
            products.
          </p>
        </div>
        <div className="w-full border-y border-stone-border bg-[#F3F4F5] flex items-center justify-center py-12 px-6 md:px-12 pt-[0px]" data-id="element-250" data-mp="2rzmswlbpi6as">
          <img src="https://cdn.magicpatterns.com/uploads/iXM1ZKJ64Xdj7QTcngTd9W/UGF_workflow.png" alt="Unified Guidance Framework workflow" className="h-auto block object-contain" style={{
          maxWidth: '85%'
        }} data-id="element-251" data-mp="2e1gc6hyw2myc" />
        </div>
      </motion.section>

      {/* Approach Section (3 Pillars) */}
      <motion.section variants={item} className="max-w-5xl mx-auto px-6 md:px-12 mb-32 text-center mt-[128px]" data-id="element-252" data-mp="306xr6i4990ac">
        <h2 className="text-3xl font-bold text-ink mb-16" data-id="element-253" data-mp="2v7tfnktuyqgu">Approach</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-254" data-mp="2qftczkmeljgk">
          <div className="flex flex-col items-center" data-id="element-255" data-mp="2lhhyuxp2smsj">
            <div className="w-16 h-16 rounded-full bg-stone-light flex items-center justify-center mb-6" data-id="element-256" data-mp="2lgysme8sfmuc">
              <MapPinIcon className="w-6 h-6 text-ink" data-id="element-257" data-mp="2hh7v7rora8gu" />
            </div>
            <h3 className="font-bold text-ink mb-2" data-id="element-258" data-mp="2cfs87u30prvt">Understand</h3>
            <p className="text-sm text-stone-medium" data-id="element-259" data-mp="27hgz5lkjrlen">
              Analyze the guidance ecosystem to uncover gaps and issues
            </p>
          </div>
          <div className="flex flex-col items-center" data-id="element-260" data-mp="22kgsp9jscgn0">
            <div className="w-16 h-16 rounded-full bg-stone-light flex items-center justify-center mb-6" data-id="element-261" data-mp="5qvpu7c6fb8v">
              <LayoutGridIcon className="w-6 h-6 text-ink" data-id="element-262" data-mp="r12ueviv5t3" />
            </div>
            <h3 className="font-bold text-ink mb-2" data-id="element-263" data-mp="98jg3xhk1zfa">Strategize</h3>
            <p className="text-sm text-stone-medium" data-id="element-264" data-mp="967400yhaja5">
              Define and align on foundational principles and framework
            </p>
          </div>
          <div className="flex flex-col items-center" data-id="element-265" data-mp="i1ms01cjy47t">
            <div className="w-16 h-16 rounded-full bg-stone-light flex items-center justify-center mb-6" data-id="element-266" data-mp="i2xy47tgzo23">
              <WrenchIcon className="w-6 h-6 text-ink" data-id="element-267" data-mp="3metec8mqpioi" />
            </div>
            <h3 className="font-bold text-ink mb-2" data-id="element-268" data-mp="3mgwcjfy1qi4b">Test & learn</h3>
            <p className="text-sm text-stone-medium" data-id="element-269" data-mp="3qh6hcpa3r2hl">
              Validate the framework through experiments and iteration
            </p>
          </div>
        </div>
      </motion.section>

      {/* Paragraph with Image Modules */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32 space-y-32" data-id="element-270" data-mp="473wm9v100z0">
        {/* Understand */}
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-271" data-mp="1ardbkpwydybb">
          <div className="lg:col-span-4" data-id="element-272" data-mp="wjtyxp2shej8">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-273" data-mp="wj21e8pmt6qb">
              Understand the guidance ecosystem
            </h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-274" data-mp="11g2aytlowrm6">
              Analyzed how guidance was generated and surfaced across Ads
              platforms to uncover gaps, inconsistencies, and opportunities to
              improve adoption.
            </p>
          </div>
          <div className="lg:col-span-8 w-full overflow-hidden" data-id="element-275" data-mp="dmaku71h55py">
            <div className="relative" data-id="element-276" data-mp="ikkgeir428c3">
              <div ref={scrollRef1} className="flex overflow-x-auto gap-6 pb-6 snap-x snap-mandatory hide-scrollbar" data-id="element-277" data-mp="iln8uv430iu4">
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-278" data-mp="rl0jvs7utfgy">
                  <div className="w-full aspect-[4/3] rounded-md border border-stone-border overflow-hidden" data-id="element-279" data-mp="1xzhxig3bh78z">
                    <img src="https://cdn.magicpatterns.com/uploads/8n49hV9rtbByqhULLBVLPX/Taxonmy.jpg" alt="Taxonomy development" className="w-full h-full object-cover" data-id="element-280" data-mp="22xt9oxh33tud" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-281" data-mp="23cgdypuf8vtx">
                    Taxonomy development
                  </p>
                </div>
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-282" data-mp="1uctjyp40usaw">
                  <div className="w-full aspect-[4/3] rounded-md border border-stone-border overflow-hidden" data-id="element-283" data-mp="1pdqe52g7wi1v">
                    <img src="https://cdn.magicpatterns.com/uploads/bc3k2YUQvH6cTTYexqNazG/Audit.jpg" alt="Experience audit" className="w-full h-full object-cover" data-id="element-284" data-mp="1kedroit9dczt" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-285" data-mp="3eqc38rf1ikyv">
                    Experience audit
                  </p>
                </div>
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-286" data-mp="183d135nhkkxa">
                  <div className="w-full aspect-[4/3] rounded-md border border-stone-border overflow-hidden" data-id="element-287" data-mp="1cw65ttiyft9d">
                    <img src="https://cdn.magicpatterns.com/uploads/jG6nPzDtpwB1VTSRJdkvXA/UXR.jpg" alt="UX research" className="w-full h-full object-cover" data-id="element-288" data-mp="z510qbwti8rn" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-289" data-mp="1vuifrhhox760">
                    UX research
                  </p>
                </div>
              </div>
              <button onClick={() => atEnd1 ? scrollLeft(scrollRef1) : scrollRight(scrollRef1)} className="absolute right-2 top-[calc(((100%-24px)*0.5)-20px)] w-10 h-10 rounded-full border border-stone-border bg-white flex items-center justify-center shadow-sm hover:shadow-md transition-shadow cursor-pointer z-10" aria-label={atEnd1 ? 'Scroll left' : 'Scroll right'} data-id="element-290" data-mp="1hwk44jur7ou7">
                {atEnd1 ? <ChevronLeftIcon className="w-5 h-5 text-ink" data-id="element-291" data-mp="3v39zkr7zxj3j" /> : <ChevronRightIcon className="w-5 h-5 text-ink" data-id="element-292" data-mp="ct84ytpdsh7u" />}
              </button>
            </div>
          </div>
        </motion.section>

        {/* Strategize */}
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-293" data-mp="3gcavnti3viqa">
          <div className="lg:col-span-4" data-id="element-294" data-mp="3q1uh96x7dqou">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-295" data-mp="3q2mcggmw1mel">
              Strategize and define the framework
            </h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-296" data-mp="3l919ea6fu28j">
              Defined core guidance principles across Ads teams and partnered
              with design system and surface owners to scale the framework
              consistently across Ads Manager.
            </p>
          </div>
          <div className="lg:col-span-8 w-full overflow-hidden" data-id="element-297" data-mp="1vwwjbmn5h7vi">
            <div className="relative" data-id="element-298" data-mp="20v7srw9j9gd7">
              <div ref={scrollRef2} className="flex overflow-x-auto gap-6 pb-6 snap-x snap-mandatory hide-scrollbar" data-id="element-299" data-mp="20s25mmkgsok4">
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-300" data-mp="29n9sttfowumy">
                  <div className="w-full aspect-[5/3] bg-stone-light rounded-md border border-stone-border overflow-hidden" data-id="element-301" data-mp="2ekrqgno9bnfn">
                    <img src="https://cdn.magicpatterns.com/uploads/iPspaYLizRzZ6b7JASWY62/Short.jpg" alt="Compelling shortened format" className="w-full h-full object-cover" data-id="element-302" data-mp="2jitd1ikw64vv" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-303" data-mp="u7ao6s5a7grg">
                    Compelling shortened format
                  </p>
                </div>
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-304" data-mp="qo23dcdl99n6">
                  <div className="w-full aspect-[5/3] bg-stone-light rounded-md border border-stone-border overflow-hidden" data-id="element-305" data-mp="lwttkf8cjm9h">
                    <video src="https://cdn.magicpatterns.com/uploads/jBF2QsWwQketYYLqAmtQwE/progressive%20disclosure.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-306" data-mp="gyscfzuhdx0n" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-307" data-mp="10lggb20azgaq">
                    Progressive disclosure
                  </p>
                </div>
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-308" data-mp="vozn3te8ybqs">
                  <div className="w-full aspect-[5/3] bg-stone-light rounded-md border border-stone-border overflow-hidden" data-id="element-309" data-mp="3la4caodr1bb5">
                    <img src="https://cdn.magicpatterns.com/uploads/xgZ6uE7ETjE8GJXkDJ6zLH/Streamline.jpg" alt="Streamline consistent features" className="w-full h-full object-cover" data-id="element-310" data-mp="3huj8x9e88058" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-311" data-mp="3cuwuiwmnwduu">
                    Streamline consistent features
                  </p>
                </div>
              </div>
              <button onClick={() => atEnd2 ? scrollLeft(scrollRef2) : scrollRight(scrollRef2)} className="absolute right-2 top-[calc(((100%-24px)*0.5)-20px)] w-10 h-10 rounded-full border border-stone-border bg-white flex items-center justify-center shadow-sm hover:shadow-md transition-shadow cursor-pointer z-10" aria-label={atEnd2 ? 'Scroll left' : 'Scroll right'} data-id="element-312" data-mp="2z1ytatnjfrjd">
                {atEnd2 ? <ChevronLeftIcon className="w-5 h-5 text-ink" data-id="element-313" data-mp="2l5tp3dj1gxgm" /> : <ChevronRightIcon className="w-5 h-5 text-ink" data-id="element-314" data-mp="21kbefpwz35d8" />}
              </button>
            </div>
          </div>
        </motion.section>
      </div>

      {/* Full Bleed Image Module */}
      <motion.section variants={item} className="mb-32" data-id="element-315" data-mp="2agkfta4ige20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-12" data-id="element-316" data-mp="26ginhrko0wze">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-317" data-mp="2kgb772xrycxv">Build to scale</h2>
          <p className="text-stone-medium mb-8" data-id="element-318" data-mp="2fi9oij49gfmx">
            Developed a scalable foundation
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/s1fgHUb6Z81S7kNkebirMA/Structure.png" alt="Build to scale structure" className="w-full mx-auto rounded-md mt-[48px] mb-[24px]" data-id="element-319" data-mp="2u7e9u96cqzz9" />
        </div>
        <div className="w-full border-y border-stone-border bg-[#EEF5F9]" data-id="element-320" data-mp="33844h9jvmkbe">
          <img src="https://cdn.magicpatterns.com/uploads/xBf23kDhBhqs9r2yaCF77o/build_to_scale.png" alt="Build to scale" className="w-full h-auto block pl-[40px] pr-[40px]" data-id="element-321" data-mp="2y9izkwtojcgm" />
        </div>
      </motion.section>

      {/* Paragraph + Image (Experimentation) */}
      <motion.section variants={item} className="max-w-7xl mx-auto px-6 md:px-12 mb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-322" data-mp="ihirerw19kuw">
        <div className="lg:col-span-5" data-id="element-323" data-mp="3muz4ioz3baic">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-324" data-mp="3mw0ip2bekd4a">
            During campaign creation
          </h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-325" data-mp="3qwalk63wenex">
            Providing guidance through the shorten component, will drive higher
            advertiser engagement and can lead to increased adoption of
            pre-flight recommendations.
          </p>
          <ul className="list-disc pl-5 text-stone-medium space-y-2" data-id="element-326" data-mp="335evpej062ab">
            <li data-id="element-327" data-mp="3851b9umazuma">Simplified and contextualized guidance</li>
            <li data-id="element-328" data-mp="383ggc3w2u9nw">Utilize progressive disclosure tactics</li>
          </ul>
        </div>
        <div className="lg:col-span-7" data-id="element-329" data-mp="30lidgi8ehwtn">
          <BeforeAfterSlider data-id="element-330" data-mp="2vn70hymt7677" />
        </div>
      </motion.section>

      {/* Full Bleed Image — Campaign Creation */}
      <motion.section variants={item} className="w-full mb-32" data-id="element-331" data-mp="2qnb4omo9ml2q">
        <div className="w-full border-y border-stone-border flex items-center justify-center px-6 md:px-12 py-12 bg-[#EEF5FE]" data-id="element-332" data-mp="2qpp395i5mff1">
          <video src="https://cdn.magicpatterns.com/uploads/b8Yk6PPNFAq6ZC5RUAvkVX/Inline tagging context.mp4" autoPlay loop muted playsInline className="w-full h-auto block px-4 py-6 md:px-[120px] md:py-[120px]" data-id="element-333" data-mp="2hno6e9qu1vdw" />
        </div>
      </motion.section>

      {/* Duplicated — During campaign creation */}
      <motion.section variants={item} className="max-w-7xl mx-auto px-6 md:px-12 mb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-334" data-mp="1ywevbw1eyyk7">
        <div className="lg:col-span-5" data-id="element-335" data-mp="1tyd9j3clsl51">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-336" data-mp="iuncsis7a4yf">
            Post campaign creation
          </h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-337" data-mp="esa7fvhd8926">
            Drive value (guidance adoption, revenue) by focusing on value-proven
            actions in prioritized and consolidated section of the page and
            applying the foundational principles.
          </p>
          <ul className="list-disc pl-5 text-stone-medium space-y-2" data-id="element-338" data-mp="3w0f4n4h0dtm0">
            <li data-id="element-339" data-mp="3w2i1o8gkwn08">
              Compelling shortened format optimized for comparing guidance at a
              glance
            </li>
            <li data-id="element-340" data-mp="3n7txymqnjddj">Utilize progressive disclosure</li>
          </ul>
        </div>
        <div className="lg:col-span-7" data-id="element-341" data-mp="3i98xqnbegtp3">
          <BeforeAfterSlider beforeImage="https://cdn.magicpatterns.com/uploads/vbNBQntk4npMEMZdBiEj1k/MFR-Before.jpg" afterImage="https://cdn.magicpatterns.com/uploads/f6bCFsQPMHhzD5AWpsm1yU/MFR-After.jpg" data-id="element-342" data-mp="3iabl4l9ike09" />
        </div>
      </motion.section>

      {/* Full Bleed Image — Post Campaign Creation */}
      <motion.section variants={item} className="w-full mb-32 overflow-hidden" data-id="element-343" data-mp="2y98obe6twpzp">
        <div className="w-full border-y border-stone-border flex items-center justify-center px-6 md:px-12 py-12 bg-[#F6FBFE] overflow-hidden" data-id="element-344" data-mp="334nztzhd69sy">
          <img src="https://cdn.magicpatterns.com/uploads/rDMj88pXKGNBfXaB7jK8FX/SYD.jpg" alt="Post campaign creation guidance" className="w-full h-auto block px-4 py-6 md:px-[120px] md:py-[120px] bg-[#F6FBFE] pl-[0px] pr-[0px] pt-[64px] pb-[64px] scale-[1.3] origin-center" data-id="element-345" data-mp="3d4536a958blr" />
        </div>
      </motion.section>

      {/* Text + Image Section */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32" data-id="element-346" data-mp="2u5kfks6gnocv">
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-347" data-mp="2u6vi6oo66q2e">
          <div className="lg:col-span-4" data-id="element-348" data-mp="21p9owpeesc5q">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-349" data-mp="21nyn2v4ixeh9">
              Onboard to the Unified Guidance Platform
            </h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-350" data-mp="1tmh6j8isaku1">
              Ensure guidance inventory is in the necessary schema/format which
              allows for seamless multi-channel delivery
            </p>
          </div>
          <div className="lg:col-span-8 w-full" data-id="element-351" data-mp="1yfk5jw6bymhm">
            <div className="w-full rounded-md overflow-hidden" data-id="element-352" data-mp="1yfse5bvf75n0">
              <img src="https://cdn.magicpatterns.com/uploads/uhqTUTZopeD9swKm686Yhq/UGP.png" alt="Unified Guidance Platform onboarding" className="w-full h-auto" data-id="element-353" data-mp="1aowiu4rr9sb0" />
            </div>
          </div>
        </motion.section>
      </div>

      {/* Results Module (Dark Background) */}
      <motion.section variants={item} className="w-full bg-ink text-white py-32 px-6 md:px-12 pb-[128px]" data-id="element-354" data-mp="1fnhj2470cbzg">
        <div className="max-w-4xl mx-auto text-center" data-id="element-355" data-mp="2r3y021y7n0of">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-356" data-mp="2r3fvmi1e9g5f">
            Impact and outcome
          </h2>
          <p className="text-xl text-stone-medium font-light leading-relaxed mb-16" data-id="element-357" data-mp="2ym5omyzweyik">
            The Benefit + Action content structure was adopted by 20+ Ads teams
            as the standard for advertiser guidance across Ads Manager.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 max-w-2xl mx-auto" data-id="element-358" data-mp="2tnuh4ux2t03i">
            <div data-id="element-359" data-mp="3he8bis34vb19">
              <div className="text-5xl font-bold mb-2" data-id="element-360" data-mp="3hdoqv3m3m8jj">Proven lift</div>
              <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-361" data-mp="3md1kq8be0fvm">
                in advertiser adoption
              </div>
            </div>

            <div data-id="element-362" data-mp="38leepgrn7szn">
              <div className="text-5xl font-bold mb-2" data-id="element-363" data-mp="38jtw8pils0i5">20+</div>
              <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-364" data-mp="3clx8hgd3j94h">
                Teams adopted
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </motion.article>;
}